import React, { useContext, useEffect, useState } from 'react';
import { Typography, useMediaQuery, useTheme } from '@material-ui/core';
import AddCircle from '@material-ui/icons/AddCircle';
import { Button, LoadingBounce } from '@michelin/acid-components';
import { useTranslation } from '@michelin/central-provider';
import { ModalSectionTitle } from 'components/ClosableDialog/ModalSectionTitle';
import DividerWithText from 'components/DividerWithText/DividerWithText';
import { SelectVehicle } from 'components/SelectVehicle/SelectVehicle';
import VehiclesDetail from 'compositions/OrderDetail/components/VehicleEdition/VehiclesDetail';
import { useDealerCanCreateVehicle, useFleetCanCreateVehicle } from 'hooks/useCanCreateVehicle';
import { useCurrentLocationIsDealer } from 'hooks/useCurrentLocationIsDealer';
import { useGoogleAnalytics } from 'hooks/useGoogleAnalytics';
import { AnalyticsAction, AnalyticsCategory } from 'hooks/useGoogleAnalytics/types';
import { ReactComponent as Tire } from 'icons/tires.svg';
import { VehicleType } from 'types/UFOTypes';
import { BREAKPOINT } from 'utils/responsivenessUtils';
import { WizardContext } from '../WizardContext';

export const getVehicleLabel = (vehicle: VehicleType) => {
  if (!vehicle.id) return 'Loading...';
  return `${vehicle.year || ''} ${vehicle.make_name || ''} ${vehicle.model_name || ''} ${vehicle.option_name || ''}`;
};

export const CurrentRouter: { history: any; match: any; unlisten: any; onChange: any } = {
  history: null,
  match: null,
  unlisten: null,
  onChange: null,
};

export const VehicleSelection = (props: { onNext: () => void }) => {
  const { onNext } = props;
  const { t } = useTranslation();
  const theme = useTheme();
  const mobileView = useMediaQuery(theme.breakpoints.down(BREAKPOINT));
  const [newVehicleForm, setNewVehicleForm] = useState(false);
  const [editVehicle, setEditVehicle] = useState(false);
  const [canCreateVehicle, setCanCreateVehicle] = useState<boolean>();
  const [canEditVehicle, setCanEditVehicle] = useState<boolean>();
  const googleAnalyticsUtils = useGoogleAnalytics();

  const isDealer = useCurrentLocationIsDealer();

  const { vehicle, setVehicle, setDisabled, fleet, provider } = useContext(WizardContext);

  const dealerCanCreateVehicleCall = useDealerCanCreateVehicle(
    fleet?.customer_number || '',
    provider?.customer_number || '',
  );
  const fleetCanCreateVehicleCall = useFleetCanCreateVehicle(fleet?.customer_number || '');

  useEffect(() => {
    if (fleet?.customer_number && (provider?.customer_number || !isDealer))
      if (isDealer) {
        if (typeof dealerCanCreateVehicleCall.data === 'boolean') setCanCreateVehicle(dealerCanCreateVehicleCall.data);
      } else {
        if (fleetCanCreateVehicleCall.data) setCanCreateVehicle(fleetCanCreateVehicleCall.data.canCreate);
        if (fleetCanCreateVehicleCall.data) setCanEditVehicle(fleetCanCreateVehicleCall.data.canUpdate);
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fleetCanCreateVehicleCall.data, dealerCanCreateVehicleCall.data]);

  if (!fleet) return null;
  if (dealerCanCreateVehicleCall.isLoading || fleetCanCreateVehicleCall.isLoading || canCreateVehicle === undefined)
    return <LoadingBounce />;

  return (
    <div style={mobileView ? { padding: 16 } : undefined}>
      {!newVehicleForm && !editVehicle && (
        <>
          <ModalSectionTitle title={t('Select Vehicle')} subtitle={t('Start by searching for a vehicle')} />
          <SelectVehicle
            fleetNumber={fleet.customer_number}
            vehicle={vehicle}
            setVehicle={setVehicle}
            setDisabled={setDisabled}
            setEditMode={
              ((canCreateVehicle && isDealer) || (canEditVehicle && !isDealer)) && vehicle?.id
                ? (v) => setEditVehicle(v)
                : undefined
            }
          />
          {canCreateVehicle && (
            <div style={{ maxWidth: 500, margin: '0 auto' }}>
              <Typography
                color="primary"
                onClick={() => {
                  googleAnalyticsUtils.event({
                    category: AnalyticsCategory.VEHICLE_MANAGEMENT,
                    action: AnalyticsAction.ADD_NEW_VEHICLE,
                    isDealer: !!isDealer,
                  });
                  setNewVehicleForm(true);
                }}
                style={{
                  fontSize: 16,
                  marginTop: 16,
                  fontWeight: 'bold',
                  cursor: 'pointer',
                }}
              >
                <AddCircle style={{ marginRight: 8, marginBottom: -8 }} color="primary" />
                {t('Add new vehicle')}
              </Typography>
            </div>
          )}
        </>
      )}
      {editVehicle && vehicle?.id && (
        <VehiclesDetail
          fleetId={fleet.customer_number}
          providerId={provider?.customer_number || ''}
          vehicleId={vehicle.id.toString()}
          action="edit"
          setVehicle={(v) => {
            setVehicle(v);
            setEditVehicle(false);
          }}
          onDiscard={() => setEditVehicle(false)}
          wizardMode
        />
      )}
      {newVehicleForm && (
        <VehiclesDetail
          fleetId={fleet.customer_number}
          providerId={provider?.customer_number || ''}
          action="add"
          setVehicle={(v) => {
            setVehicle(v);
            setNewVehicleForm(false);
          }}
          onDiscard={() => setNewVehicleForm(false)}
          wizardMode
        />
      )}
      {isDealer && (
        <div style={{ margin: 16, textAlign: 'center' }}>
          <DividerWithText>{t('OR')}</DividerWithText>
          <Button
            style={{ marginTop: 16 }}
            onClick={() => {
              setVehicle(null);
              onNext();
            }}
            variant="contained"
            color="primary"
            size="small"
            startIcon={<Tire fill={theme.palette.background.paper} style={{ width: 40, height: 40, margin: -4 }} />}
          >
            {t('Create a Mounted Wheel Order')}
          </Button>
        </div>
      )}
    </div>
  );
};
